import React, { useCallback } from 'react';

import { ReactComponent as Lab } from 'assets/icons/lab.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/pwEyeShow.svg';
import {
  FEDEX_TRACKING_NO_TRACKING_NUMBER,
  FEDEX_TRACKING_URL,
  genericActionsIds,
  pageIds,
} from 'utilities/constants';
import { TestKitsTableData } from '../DNATestKitTable/interfaces';
import ButtonComponent from 'components/button/buttonComponent';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import {
  Dnavisit_Test_Statuses_Enum,
  Test_Types_Enum,
  useGetSignedUrlFromStorageMutation,
} from 'graphql/generated/hasura';
import { useGetPage } from 'hooks/useGetPage';
import StorageService from 'utilities/storageService';
import {
  APPOINTMENT_DETAILS,
  APPOINTMENT_STORAGE,
} from '../../../my-appointments/components/scheduled-a-appointment/routePath';
import { useGetFhirProviderByCodexIdLazyQuery } from 'graphql/generated/remote-schema-hasura';
import { parseServiceType } from '../../../my-appointments/components/select-a-provider/ProviderCardProps';
import {
  DNA_REGISTER_PAGE,
  HORMONE_QUESTIONNAIRE_FULL,
  MICROBIOME_QUESTIONNAIRE_FULL,
  MY_APPOINTMENTS_DETAILS,
  MY_APPOINTMENTS_MY_VISIT_SUMMARY,
  MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT,
  SCHEDULE_DNA_VISIT,
  TEST_KITS_MANAGEMENT,
} from 'utilities/routes';
import { useNavigate } from 'react-router-dom';
import { useGetFileFromStorage } from '../../../../hooks/useGetFileFromStorage';

export const DnaTypes = [Test_Types_Enum.DnaVitamin, Test_Types_Enum.DnaSkin];

const TrackingButtonClick: React.FC<{
  type: Test_Types_Enum;
  trackingNumber?: string;
  label: string;
}> = ({ label, trackingNumber }) => {
  const handleZrtTrackPackageClick = useCallback(() => {
    window.open(
      `${
        trackingNumber ? FEDEX_TRACKING_URL : FEDEX_TRACKING_NO_TRACKING_NUMBER
      }${trackingNumber}`,
      '_blank',
      'noreferrer',
    );
  }, [trackingNumber]);

  const handleClick = useCallback(() => {
    if (!trackingNumber) {
      return console.error('Not tracking number');
    }
    return handleZrtTrackPackageClick();
  }, [handleZrtTrackPackageClick, trackingNumber]);

  if (!trackingNumber) {
    return null;
  }

  return (
    <ButtonComponent
      type="outlined"
      className="whitespace-nowrap !w-full"
      onClick={handleClick}
      testID={'track-package'}
    >
      {label}
    </ButtonComponent>
  );
};

const PendingButtonClick: React.FC<{
  appointmentId: string;
  testType: Test_Types_Enum;
}> = ({ appointmentId, testType }) => {
  const navigate = useNavigate();

  const handleAppointmentDetailsClick = useCallback(() => {
    if (DnaTypes.includes(testType)) {
      return navigate(SCHEDULE_DNA_VISIT);
    }
    window.open(
      `${MY_APPOINTMENTS_DETAILS}/${appointmentId}`,
      '_blank',
      'noreferrer',
    );
  }, [appointmentId, navigate, testType]);

  return (
    <ButtonComponent
      className="max-desktop:mt-2 !text-sm bg-transparent text-clc-blue overflow-ellipsis overflow-hidden whitespace-nowrap"
      onClick={handleAppointmentDetailsClick}
      testID={'appointment-legal-doc-edit'}
    >
      Appointment Details
    </ButtonComponent>
  );
};

const appointmentStorage = new StorageService(
  APPOINTMENT_STORAGE,
  sessionStorage,
);

const ScheduleButtonClick: React.FC<{
  handleScheduleClick: () => void;
  label: string;
  loading: boolean;
  hasMaxPendingAppointments?: boolean;
}> = ({ handleScheduleClick, label, loading, hasMaxPendingAppointments }) => {
  return (
    <ButtonComponent
      className="max-desktop:mt-2 !text-sm overflow-ellipsis overflow-hidden whitespace-nowrap"
      onClick={handleScheduleClick}
      testID={'schedule'}
      disabled={loading || hasMaxPendingAppointments}
    >
      {label}
    </ButtonComponent>
  );
};

const CompleteQuestionnaireClick: React.FC<{
  type: Test_Types_Enum;
  trackingNumber?: string;
  label: string;
  testKitIdNumber: string;
}> = ({ type, trackingNumber, label, testKitIdNumber }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (DnaTypes.includes(type)) {
      navigate(`${TEST_KITS_MANAGEMENT}/${DNA_REGISTER_PAGE}`, {
        state: {
          testKitData: {
            testKitIdNumber,
            testType: type,
            testKitTrackingNumber: trackingNumber,
          },
        },
      });
    }
    if (type === Test_Types_Enum.DsGiMap) {
      navigate(MICROBIOME_QUESTIONNAIRE_FULL, {
        state: {
          testKitData: {
            testKitIdNumber,
            testType: type,
            testKitTrackingNumber: trackingNumber,
          },
        },
      });
      return;
    }

    navigate(HORMONE_QUESTIONNAIRE_FULL, {
      state: {
        testKitData: {
          testKitIdNumber,
          testType: type,
          testKitTrackingNumber: trackingNumber,
        },
      },
    });
  };

  return (
    <ButtonComponent
      className="max-desktop:mt-2 !text-sm overflow-ellipsis overflow-hidden whitespace-nowrap"
      onClick={handleClick}
      testID={'schedule'}
    >
      {label}
    </ButtonComponent>
  );
};

type CompleteButtonsProps = {
  handleScheduleClick: () => void;
} & TestKitsTableData;

const CompleteButtons: React.FC<CompleteButtonsProps> = ({
  patientPdfId,
  appointmentId,
  handleScheduleClick,
  patientVisitSummaryPdfId,

  test_type,
}) => {
  const { data: genericAction } = useGetGenericActions({
    locale: 'en',
    genericActionId: [
      genericActionsIds.SUMMARY,
      genericActionsIds.TEST_KIT_RESULT,
    ],
  });

  const { downloadFileFromUrl } = useGetFileFromStorage();

  const [getUrl, { loading: viewLoading }] =
    useGetSignedUrlFromStorageMutation();

  const handleClickSummary = (appointmentId: string) => {
    const url = `${MY_APPOINTMENTS_MY_VISIT_SUMMARY}/${appointmentId}`;
    window.open(url, '_blank');
  };

  const handleOnView = async () => {
    if (viewLoading) {
      return;
    }
    const { data } = await getUrl({
      variables: { fileId: String(patientPdfId) },
    });
    window.open(data?.GetSignUrlFormStorage?.url, '_blank');
  };

  const download = async (pdfId: string) => {
    if (viewLoading) {
      return;
    }
    const { data } = await getUrl({
      variables: { fileId: String(pdfId) },
    });
    if (!data?.GetSignUrlFormStorage || !data?.GetSignUrlFormStorage?.url) {
      return;
    }
    return downloadFileFromUrl(data.GetSignUrlFormStorage.url);
  };

  if (DnaTypes.includes(test_type)) {
    return (
      <div className="flex max-mobile:!block items-center gap-2.5">
        {patientVisitSummaryPdfId ? (
          <ButtonComponent
            className="!text-sm font-bold"
            type="underline"
            Icon={EyeIcon}
            iconPosition="left"
            testID={'eye-icon'}
            // disabled={!patientVisitSummaryPdfId}
            containerClassName="!gap-1.5 max-mobile:justify-start"
            stroke="stroke-transparent"
            onClick={() => {
              // if (patientVisitSummaryPdfId) {
              //   return download(patientVisitSummaryPdfId as string);
              // }
              return handleClickSummary(appointmentId as string);
            }}
          >
            {genericAction?.[genericActionsIds.SUMMARY]?.summary}
          </ButtonComponent>
        ) : (
          <ButtonComponent
            className="!text-sm font-bold"
            testID={'book-visit'}
            containerClassName="!gap-1.5 max-mobile:justify-start"
            onClick={() => handleScheduleClick()}
          >
            Book visit
          </ButtonComponent>
        )}
        <ButtonComponent
          className="!text-sm font-bold"
          type="underline"
          containerClassName="!gap-1.5 max-mobile:justify-start"
          Icon={Lab}
          stroke="stroke-transparent"
          iconPosition="left"
          disabled={!patientPdfId}
          testID={'download-icon'}
          onClick={() => {
            if (patientPdfId) {
              return download(patientPdfId as string);
            }
            return handleOnView();
          }}
        >
          Test results
        </ButtonComponent>
      </div>
    );
  }

  return (
    <div className="flex max-mobile:!block items-center gap-2.5 desktop:py-4">
      <ButtonComponent
        className="!text-sm font-bold"
        type="underline"
        Icon={EyeIcon}
        iconPosition="left"
        testID={'eye-icon'}
        containerClassName="!gap-1.5 max-mobile:justify-start"
        stroke="stroke-transparent"
        onClick={() => {
          return handleClickSummary(appointmentId as string);
        }}
      >
        {genericAction?.[genericActionsIds.SUMMARY]?.summary}
      </ButtonComponent>
      <ButtonComponent
        className="!text-sm font-bold"
        type="underline"
        containerClassName="!gap-1.5 max-mobile:justify-start"
        Icon={Lab}
        stroke="stroke-transparent"
        iconPosition="left"
        disabled={!patientPdfId}
        testID={'download-icon'}
        onClick={() => {
          if (patientPdfId) {
            return download(patientPdfId as string);
          }
          return handleOnView();
        }}
      >
        {genericAction?.[genericActionsIds.TEST_KIT_RESULT]?.testKitResult}
      </ButtonComponent>
    </div>
  );
};

const ActionButtons: React.FC<TestKitsTableData> = (tesKitData) => {
  const navigate = useNavigate();
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.TEST_KITS_AND_RESULTS,
  });

  const [getProvider, { loading: appointmentLoading }] =
    useGetFhirProviderByCodexIdLazyQuery();

  const handleScheduledAppointmentClick = useCallback(async () => {
    const provider = await getProvider({
      variables: { codexProviderId: tesKitData.providerCodexId },
    });
    if (!provider.data) {
      appointmentStorage.addData({
        diagnosticReportIds: [tesKitData.fhirId],
      });
      return navigate({
        pathname: `${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}`,
      });
    }

    const { acuityCalendarId, acuityOwnerId, serviceOfferings } =
      provider.data?.getFHIRProviderbyCodexIdArgument.providers[0].providers ||
      {};

    const serviceOfferingsAppointment = (serviceOfferings || []).map(
      (serviceOffering) => ({
        price: serviceOffering.price,
        active: serviceOffering.active,
        serviceType: parseServiceType(serviceOffering.serviceType),
      }),
    );

    appointmentStorage.addData({
      diagnosticReportIds: [tesKitData.fhirId],
      selectedProvider: {
        codexProviderId: tesKitData.providerCodexId,
        calendarId: acuityCalendarId,
        ownerId: acuityOwnerId,
        serviceOfferings: serviceOfferingsAppointment,
      },
    });
    navigate({
      pathname: `${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}/${APPOINTMENT_DETAILS}`,
    });
  }, [getProvider, navigate, tesKitData]);

  if (!locale || loading) {
    return null;
  }

  const statusButtons = (() => {
    switch (tesKitData.status) {
      case Dnavisit_Test_Statuses_Enum.KitNotReceived:
        return (
          <div className="flex justify-center items-center gap-1.5">
            {/*{DnaTypes.includes(tesKitData.test_type) && (*/}
            {/*  <ButtonComponent*/}
            {/*    type="outlined"*/}
            {/*    className="!w-full"*/}
            {/*    onClick={handleScheduledAppointmentClick}*/}
            {/*    testID="dna-detail"*/}
            {/*  >*/}
            {/*    Details*/}
            {/*  </ButtonComponent>*/}
            {/*)}*/}
            {!tesKitData.trackingNumber &&
            !DnaTypes.includes(tesKitData.test_type) ? (
              <p className="text-med-gray-3 font-medium text-sm">
                Tracking number not provided
              </p>
            ) : (
              <TrackingButtonClick
                type={tesKitData.test_type}
                label={locale.trackPackage}
                trackingNumber={tesKitData.trackingNumber}
              />
            )}
          </div>
        );

      case Dnavisit_Test_Statuses_Enum.ReceivedByLab:
        return null;
      case Dnavisit_Test_Statuses_Enum.ResultsWithProvider:
        return (
          <ScheduleButtonClick
            handleScheduleClick={handleScheduledAppointmentClick}
            label={
              DnaTypes.includes(tesKitData.test_type)
                ? 'Schedule DNA visit'
                : locale.scheduleAppointment
            }
            loading={appointmentLoading}
            hasMaxPendingAppointments={tesKitData.hasMaxPendingAppointments}
          />
        );

      case Dnavisit_Test_Statuses_Enum.AppointmentScheduled:
        return (
          <PendingButtonClick
            appointmentId={tesKitData.appointmentId as string}
            testType={tesKitData.test_type}
          />
        );

      case Dnavisit_Test_Statuses_Enum.Complete:
        return (
          <CompleteButtons
            {...tesKitData}
            handleScheduleClick={handleScheduledAppointmentClick}
          />
        );

      case Dnavisit_Test_Statuses_Enum.Issue:
        return (
          <div className="flex flex-col gap-1.5 -my-4">
            <p className="text-med-gray-3 font-medium">Contact us:</p>
            <a
              className="underline text-clc-blue font-medium"
              href="mailto:decode_me@gmail.com"
            >
              decode_me@gmail.com
            </a>
          </div>
        );
      case Dnavisit_Test_Statuses_Enum.Canceled:
        return null;

      default:
        return null;
    }
  })();

  const questionnaireButton = tesKitData.questionnaireRequired ? (
    <CompleteQuestionnaireClick
      type={tesKitData.test_type}
      label={locale.completeQuestionnaire || 'Complete Questionnaire'}
      trackingNumber={tesKitData.trackingNumber}
      testKitIdNumber={tesKitData.swabCode}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-2">
      {questionnaireButton}
      {statusButtons}
    </div>
  );
};

export default ActionButtons;
